// Defaults, examples and small display helpers for the CAN Raw Frame Decoder. All decoding,
// DBC generation and validation happens in the backend (tools/can_frame_decoder_tool.py);
// this file only builds the request and labels things.

export const MAX_FRAMES = 32; // mirrors MAX_FRAMES in the backend

// ISO 11898-1 CAN FD DLC code -> payload bytes (mirrors CAN_FD_DLC_TO_LENGTH).
export const CAN_FD_DLC_TO_LENGTH = [0, 1, 2, 3, 4, 5, 6, 7, 8, 12, 16, 20, 24, 32, 48, 64];

export const dlcOptions = (protocol) => {
  const fd = protocol === 'canfd';
  return CAN_FD_DLC_TO_LENGTH.slice(0, fd ? 16 : 9).map((len, dlc) => ({
    value: String(dlc),
    label: dlc > 8 ? `${dlc} → ${len} bytes` : `${dlc} (${len} byte${len === 1 ? '' : 's'})`,
  }));
};

export const payloadLengthFor = (protocol, dlc) => {
  const n = Number(dlc);
  if (!Number.isInteger(n) || n < 0) return null;
  if (protocol === 'canfd') return n <= 15 ? CAN_FD_DLC_TO_LENGTH[n] : null;
  return n <= 8 ? n : null;
};

// Same tokenizing as the backend's parse_data, used only for the live "n of m bytes" hint
// and the "Fit to DLC" helper. Returns null if the text isn't clean hex bytes.
export function parseHexBytes(text) {
  const s = (text || '').trim().replace(/0x/gi, ' ');
  if (!s) return [];
  let tokens = s.split(/[\s,;:-]+/).filter(Boolean);
  if (tokens.length === 1 && tokens[0].length > 2) {
    const t = tokens[0];
    if (t.length % 2) return null;
    tokens = t.match(/.{2}/g);
  }
  if (!tokens.every((t) => /^[0-9a-f]{1,2}$/i.test(t))) return null;
  return tokens.map((t) => parseInt(t, 16));
}

export const formatBytes = (bytes) => bytes.map((b) => b.toString(16).toUpperCase().padStart(2, '0')).join(' ');

let frameSeq = 0;
export const nextFrameId = () => `frame-${++frameSeq}`;

const blankSignal = (n) => ({
  name: `Signal_${n}`,
  startBit: '0',
  length: '8',
  byteOrder: 'intel',
  valueType: 'unsigned',
  factor: '1',
  offset: '0',
  min: '',
  max: '',
  unit: '',
  receiver: '',
});

export function newFrame(existing = []) {
  const n = existing.length + 1;
  // Next free standard ID after the highest one in use, so a new frame doesn't silently
  // merge into an existing message.
  const used = existing
    .filter((f) => f.idFormat === 'standard')
    .map((f) => parseInt(String(f.canId).replace(/^0x/i, ''), 16))
    .filter((x) => Number.isFinite(x));
  const nextId = used.length ? Math.min(Math.max(...used) + 1, 0x7ff) : 0x100;
  return {
    id: nextFrameId(),
    canId: `0x${nextId.toString(16).toUpperCase()}`,
    idFormat: 'standard',
    protocol: 'can',
    dlc: '8',
    data: '00 00 00 00 00 00 00 00',
    messageName: `Message_${n}`,
    transmitter: '',
    signal: blankSignal(n),
  };
}

const fdPayload = (() => {
  const bytes = new Array(64).fill(0);
  bytes[12] = 0xa0; // ChargerOutputVoltage raw 4000 (0x0FA0), Intel -> LSB byte first
  bytes[13] = 0x0f;
  return formatBytes(bytes);
})();

// Three frames covering Intel, Motorola (signed, crossing a byte boundary) and a 64-byte
// extended CAN FD frame. Values are illustrative, not from a real OEM database.
export const EXAMPLE_FRAMES = [
  {
    canId: '0x180', idFormat: 'standard', protocol: 'can', dlc: '8',
    data: '10 27 00 00 00 00 00 00', messageName: 'VehicleData', transmitter: 'ECU_VCU',
    signal: {
      name: 'VehicleSpeed', startBit: '0', length: '16', byteOrder: 'intel', valueType: 'unsigned',
      factor: '0.01', offset: '0', min: '0', max: '250', unit: 'km/h', receiver: 'ECU_BMS',
    },
  },
  {
    canId: '0x3C2', idFormat: 'standard', protocol: 'can', dlc: '8',
    data: 'FE 0C 0F A0 00 00 00 00', messageName: 'BMS_PackStatus', transmitter: 'ECU_BMS',
    signal: {
      name: 'PackCurrent', startBit: '7', length: '16', byteOrder: 'motorola', valueType: 'signed',
      factor: '0.1', offset: '0', min: '-3276.8', max: '3276.7', unit: 'A', receiver: 'ECU_VCU',
    },
  },
  {
    canId: '0x18FF50E5', idFormat: 'extended', protocol: 'canfd', dlc: '15',
    data: fdPayload, messageName: 'OBC_Status_FD', transmitter: 'ECU_OBC',
    signal: {
      name: 'ChargerOutputVoltage', startBit: '96', length: '16', byteOrder: 'intel', valueType: 'unsigned',
      factor: '0.1', offset: '0', min: '0', max: '1000', unit: 'V', receiver: 'ECU_BMS',
    },
  },
];

export const DEFAULT_FRAMES = [EXAMPLE_FRAMES[0]];

export const withIds = (frames) => frames.map((f) => ({ ...f, signal: { ...f.signal }, id: nextFrameId() }));

export const toPayloadFrame = (f) => ({
  canId: f.canId, idFormat: f.idFormat, protocol: f.protocol, dlc: f.dlc, data: f.data,
  messageName: f.messageName, transmitter: f.transmitter, signal: { ...f.signal },
});

export const CATEGORY_LABELS = {
  syntax: 'DBC syntax',
  canIds: 'CAN IDs',
  dlc: 'DLC / payload',
  positions: 'Signal positions',
  lengths: 'Signal lengths',
  overlaps: 'Signal overlaps',
  byteOrder: 'Byte order',
  scaling: 'Scaling',
  minMax: 'Min / Max',
  duplicates: 'Duplicate names',
  consistency: 'Consistency',
};
